import { useTranslation } from "react-i18next";
import InclusionPulsePng from "../../styles/illustrations/InclusionPulse.png";
import {
  BENCHMARKING_SCORE,
  GetDemographicGroupsResponse,
  GetSurveyChapterScoringResponse,
} from "../../types/workplaceCulture";
import {
  getDemographicTotalScore,
  getOrganizationDemographic,
} from "../../utils/WorkplaceCulture.utils";
import { ScoreCard } from "../ScoreCard/ScoreCard";
import { Paper } from "../ui/Paper/Paper";
import { Typography } from "../ui/Typography/Typography";
import { IconTypes } from "../ui/icons";
import { Icon } from "../ui/icons/Icon";
import "./ReportIntro.scss";

interface ReportIntroProps {
  companyName: string;
  demographicsData?: GetDemographicGroupsResponse;
  chapterScoringData?: GetSurveyChapterScoringResponse;
}

export const ReportIntro = ({
  companyName,
  demographicsData,
  chapterScoringData,
}: ReportIntroProps) => {
  const { t } = useTranslation("reportPage");

  const orgDemographic = getOrganizationDemographic(demographicsData);
  const overAllScore = orgDemographic?.score || 0;
  const chapters = chapterScoringData?.chapters || [];

  // Sort chapters by score to get lowest and highest
  const sortedChapters = [...chapters].sort((a, b) => {
    const scoreA = getDemographicTotalScore(a.demographic);
    const scoreB = getDemographicTotalScore(b.demographic);
    return scoreA - scoreB;
  });

  const chapterName1 = sortedChapters[0]?.name;
  const chapterScore1 = getDemographicTotalScore(
    sortedChapters[0]?.demographic
  );

  const chapterName2 = sortedChapters[1]?.name;
  const chapterScore2 = getDemographicTotalScore(
    sortedChapters[1]?.demographic
  );

  const chapterName3 = sortedChapters[2]?.name;
  const chapterScore3 = getDemographicTotalScore(
    sortedChapters[2]?.demographic
  );

  return (
    <Paper shadow className="ReportIntro">
      <div className="ReportIntro__header">
        <div className="ReportIntro__header__title">
          <Icon type={IconTypes.Bulb} size={32} />
          <Typography tagVariant="h2" desktop="h2" color="darkerStoneBlue">
            {t("intro.title")}
          </Typography>
        </div>
        <img
          src={InclusionPulsePng}
          alt="Inclusion Pulse"
          className="ReportIntro__header__decorationImage"
        />
      </div>

      <div className="ReportIntro__content">
        <div className="ReportIntro__content__text">
          <div className="ReportIntro__block">
            <Typography desktop="body2" color="darkerStoneBlue">
              {t("intro.firstBlock")}
            </Typography>
          </div>

          <div className="ReportIntro__separator" />

          <div className="ReportIntro__block">
            <Typography desktop="body2" color="darkerStoneBlue">
              {t("intro.secondBlock", {
                companyName,
                overAllScore,
                chapterName1,
                chapterName2,
                chapterScore1,
                chapterScore2,
                chapterName3,
                chapterScore3,
                interpolation: { escapeValue: false },
              })}
            </Typography>
          </div>

          <div className="ReportIntro__separator" />

          <div className="ReportIntro__block">
            <Typography desktop="body2" color="darkerStoneBlue">
              {t("intro.thirdBlock")}
            </Typography>
          </div>
        </div>

        <div className="ReportIntro__content__scoreSection">
          <ScoreCard
            title={t("intro.scoreTitle", { companyName })}
            score={overAllScore}
            scoreSize="medium"
            label={t("intro.benchmarkingLabel", { score: BENCHMARKING_SCORE })}
          />
        </div>
      </div>
    </Paper>
  );
};
